/**
 * Class constants for the search surfaces, kept out of the JSX so the eight
 * interaction states of each element can be audited in one place.
 *
 * Two conventions run through all of them:
 *  - Hover is gated behind `[@media(hover:hover)]:` so a tap on a touch device
 *    doesn't leave the element stuck in its hover skin.
 *  - Every transition carries `motion-reduce:transition-none`, and every press
 *    scale carries `motion-reduce:active:scale-100`.
 *
 * Focus rings use `--primary-ink`, never `--ring`: `--ring` is oklch(0.87 0 0),
 * roughly 1.3:1 on white, which fails WCAG SC 1.4.11 outright.
 */

const FOCUS_RING = 'focus-visible:ring-primary-ink focus-visible:ring-2 focus-visible:outline-none';

export const ZONE = {
  /** The pill itself. No `overflow-hidden` — it would clip the active zone's lift. */
  pill: 'relative flex items-stretch rounded-full border border-pill-border bg-white p-1.5 shadow-card',

  base: [
    'group/zone relative flex min-w-0 flex-1 flex-col justify-center rounded-full px-6 py-2.5 text-left',
    'transition-[background-color,box-shadow] duration-200 ease-morph motion-reduce:transition-none',
    '[@media(hover:hover)]:hover:bg-pill',
    // Inset, because a ring on a flush zone would otherwise be clipped by its neighbour.
    'focus-visible:ring-primary-ink focus-visible:ring-2 focus-visible:ring-inset focus-visible:outline-none',
    // The headline state: the open zone lifts out of the pill.
    'data-[open=true]:z-10 data-[open=true]:bg-white data-[open=true]:shadow-zone',
    'aria-[invalid=true]:ring-destructive aria-[invalid=true]:ring-1',
  ].join(' '),

  label: 'text-[0.6875rem] font-semibold tracking-[0.08em] text-muted-foreground uppercase',
  valueFilled: 'truncate text-sm font-semibold text-foreground',
  valueEmpty: 'truncate text-sm font-normal text-muted-foreground',

  /** Fades out when either neighbour is hovered or open, so it can't cut the lift. */
  divider: 'my-2 w-px shrink-0 self-stretch bg-pill-border transition-opacity duration-200 motion-reduce:transition-none',
} as const;

export const TRIGGER = {
  inline: [
    'flex h-14 w-full items-center gap-3 rounded-full bg-white px-5 pr-2 text-left',
    'border border-pill-border shadow-card',
    'transition-shadow duration-200 ease-morph motion-reduce:transition-none',
    '[@media(hover:hover)]:hover:shadow-zone',
    'active:scale-[0.985] motion-reduce:active:scale-100',
    FOCUS_RING,
    'focus-visible:ring-offset-2',
  ].join(' '),

  floating: [
    'fixed bottom-24 left-1/2 z-50 flex h-13 -translate-x-1/2 items-center gap-2.5 rounded-full',
    'bg-white px-5 pr-2 text-left shadow-dock ring-1 ring-black/5',
    'transition-transform duration-200 ease-morph motion-reduce:transition-none',
    'active:scale-[0.97] motion-reduce:active:scale-100',
    FOCUS_RING,
  ].join(' '),

  icon: 'bg-primary text-primary-foreground flex size-10 shrink-0 items-center justify-center rounded-full',
} as const;

export const CARD = {
  base: [
    'group/card relative overflow-hidden rounded-card bg-surface-muted text-left cursor-pointer',
    'ring-1 ring-pill-border transition-[box-shadow,transform] duration-200 ease-morph motion-reduce:transition-none',
    '[@media(hover:hover)]:hover:ring-primary [@media(hover:hover)]:hover:shadow-zone',
    'active:scale-[0.98] motion-reduce:active:scale-100',
    FOCUS_RING,
    'focus-visible:ring-offset-2',
    'data-[selected=true]:ring-primary data-[selected=true]:ring-2',
  ].join(' '),

  // 16/10 rather than 4/3: the quick-pick row now sits above this grid, and a
  // squarer card pushed the second row of destinations below the fold on a 375px
  // viewport. Wide-and-short keeps both sections visible without scrolling.
  media: 'aspect-[16/10] w-full object-cover transition-transform duration-300 ease-morph motion-reduce:transition-none [@media(hover:hover)]:group-hover/card:scale-105',
  scrim: 'absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent p-2.5 pt-6',
  title: 'line-clamp-1 text-[0.8125rem] leading-snug font-semibold text-white',
  subtitle: 'mt-px line-clamp-1 text-[0.6875rem] text-white/75',
  check: 'bg-primary text-primary-foreground absolute top-1.5 right-1.5 flex size-5 items-center justify-center rounded-full shadow-sm',
} as const;

export const ROW = {
  base: [
    'flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left',
    'transition-colors duration-150 motion-reduce:transition-none',
    '[@media(hover:hover)]:hover:bg-pill',
    FOCUS_RING,
    'data-[selected=true]:bg-primary/10',
  ].join(' '),
  thumb: 'size-11 shrink-0 overflow-hidden rounded-lg object-cover',
  name: 'truncate text-sm font-medium text-foreground',
  meta: 'truncate text-xs text-muted-foreground',
} as const;

export const CHIP = {
  base: [
    'flex shrink-0 snap-start flex-col items-center rounded-chip border border-pill-border bg-white px-4 py-0.5 cursor-pointer',
    'transition-colors duration-150 motion-reduce:transition-none',
    '[@media(hover:hover)]:hover:border-primary [@media(hover:hover)]:hover:bg-pill',
    'active:scale-95 motion-reduce:active:scale-100',
    FOCUS_RING,
    'focus-visible:ring-offset-1',
    'aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-foreground aria-pressed:shadow-card',
    'aria-pressed:hover:border-primary aria-pressed:hover:bg-primary aria-pressed:text-primary-foreground aria-pressed:shadow-card',
  ].join(' '),
  label: 'text-xs leading-tight font-semibold whitespace-nowrap',
  sub: 'text-[0.6875rem] leading-tight',

  /** Quick-pick chip: a small thumb plus the name, so the row reads as places rather than tags. */
  quickPick: [
    'flex shrink-0 snap-start items-center gap-2 rounded-chip border border-pill-border bg-white py-1 pr-3.5 pl-1',
    'transition-colors duration-150 motion-reduce:transition-none',
    '[@media(hover:hover)]:hover:border-primary/50 [@media(hover:hover)]:hover:bg-pill',
    'active:scale-[0.97] motion-reduce:active:scale-100',
    FOCUS_RING,
    'data-[selected=true]:border-primary data-[selected=true]:bg-primary/10',
  ].join(' '),
  quickPickThumb: 'size-7 shrink-0 rounded-full object-cover',
  quickPickLabel: 'max-w-[10rem] truncate text-[0.8125rem] font-medium whitespace-nowrap',
} as const;

export const CTA = {
  base: [
    'bg-primary text-primary-foreground inline-flex items-center justify-center gap-2 rounded-full font-semibold',
    'shadow-card transition-[background-color,transform] duration-200 ease-morph motion-reduce:transition-none',
    '[@media(hover:hover)]:hover:bg-brand-orange-hover',
    'active:scale-[0.98] motion-reduce:active:scale-100',
    'disabled:pointer-events-none disabled:opacity-70',
    FOCUS_RING,
    'focus-visible:ring-offset-2',
  ].join(' '),
} as const;

/**
 * Two real defects in the shared Calendar, patched here rather than in
 * `ui/calendar.tsx` (107 `variant="ghost"` call sites make that variant unsafe to touch):
 *
 *  - `CalendarDayButton` uses `variant="ghost"`, which carries `hover:bg-transparent`,
 *    so days currently have no hover feedback at all.
 *  - The built-in focus ring is `ring-ring/50`, and `--ring` fails contrast, so
 *    keyboard focus is effectively invisible.
 */
export const CALENDAR_CLASSNAMES = {
  day: [
    '[&_[data-slot=button]]:transition-colors [&_[data-slot=button]]:motion-reduce:transition-none',
    '[@media(hover:hover)]:[&_[data-slot=button]:not([data-selected-single=true])]:hover:bg-pill',
    'group-data-[focused=true]/day:ring-primary-ink group-data-[focused=true]/day:ring-2',
  ].join(' '),
  // The default `bg-accent` for today is a near-white grey that reads as a weak
  // selection. An underdot can't be confused with the real selected state.
  today: 'relative bg-transparent after:absolute after:inset-x-[38%] after:bottom-1 after:h-0.5 after:rounded-full after:bg-primary',
} as const;
